import { IAvailableTimeSlot, IDateAvailability } from "../models/IAppointmentWizard";

// Generate time slots based on studio time step (30min or 60min)
export const generateTimeSlots = (
  startHour: number = 9, 
  endHour: number = 18, 
  timeStep: number = 60 // minutes - matches studio.timeStep from AppContext
): string[] => {
  const slots: string[] = [];
  let currentTime = startHour * 60; // convert to minutes
  const endTime = endHour * 60;
  
  while (currentTime < endTime) {
    const hours = Math.floor(currentTime / 60);
    const minutes = currentTime % 60;
    const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    slots.push(timeString);
    currentTime += timeStep;
  }
  
  return slots;
};

// Generate time slots based on real studio working hours
export const generateTimeSlotsFromWorkingHours = (
  studioHours: { [key: string]: { slots: Array<{ startTime: string; endTime: string }>, isWorkingDay: boolean } },
  date: Date,
  timeStep: number = 30 // Default 30-minute intervals for appointments
): string[] => {
  const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const dayHours = studioHours[dayOfWeek.toString()];
  
  if (!dayHours || !dayHours.isWorkingDay || dayHours.slots.length === 0) {
    return []; // Not a working day or no time slots
  }
  
  const allSlots: string[] = [];
  
  // Generate slots for each time period
  dayHours.slots.forEach(slot => {
    // Parse start and end times
    const [startHour, startMinute] = slot.startTime.split(':').map(Number);
    const [endHour, endMinute] = slot.endTime.split(':').map(Number);
    
    let currentTime = startHour * 60 + startMinute; // convert to minutes
    const endTime = endHour * 60 + endMinute;
    
    while (currentTime < endTime) {
      const hours = Math.floor(currentTime / 60);
      const minutes = currentTime % 60;
      const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      allSlots.push(timeString);
      currentTime += timeStep;
    }
  });
  
  // Remove duplicates and sort
  return [...new Set(allSlots)].sort();
};

// Generate mock data for the next 30 days
const generateMockTimeSlots = (): { [dateKey: string]: IAvailableTimeSlot[] } => {
  const slots: { [dateKey: string]: IAvailableTimeSlot[] } = {};
  const today = new Date();
  
  for (let i = 0; i < 30; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    const dateKey = date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    
    // Generate different availability patterns for different days
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isToday = i === 0;
    
    // Base time slots (30-minute intervals from 9 AM to 6 PM)
    const timeSlots = [
      '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
      '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
      '15:00', '15:30', '16:00', '16:30', '17:00', '17:30'
    ];
    
    const daySlots: IAvailableTimeSlot[] = [];
    
    timeSlots.forEach((time, timeIndex) => {
      // Employees: 1 (Владимир), 2 (Ивайло), 3 (Адам)
      [1, 2, 3].forEach(employeeId => {
        let isAvailable = true;
        
        // Different availability patterns
        if (isToday) {
          // Today - some slots are busy
          isAvailable = Math.random() > 0.3; // 70% available
        } else if (isWeekend) {
          // Weekend - limited hours
          if (timeIndex < 2 || timeIndex > 12) isAvailable = false; // Only 10 AM - 4 PM
          else isAvailable = Math.random() > 0.2; // 80% available
        } else {
          // Weekdays - good availability
          isAvailable = Math.random() > 0.15; // 85% available
        }
        
        // Some days have no availability for specific employees (day off)
        if (i % 7 === 3 && employeeId === 3) isAvailable = false; // Adam has Wednesdays off
        if (i % 14 === 7 && employeeId === 2) isAvailable = false; // Ивайло has every other Sunday off
        
        daySlots.push({
          time,
          isAvailable,
          employeeId
        });
      });
    });
    
    slots[dateKey] = daySlots;
  }
  
  return slots;
};

// Mock available slots for different dates
export const mockTimeSlots: { [dateKey: string]: IAvailableTimeSlot[] } = generateMockTimeSlots();

// Helper function to get available slots for a date (optionally filtered by employee)
export const getAvailableSlotsForDate = (date: Date, employeeId?: string | number): IAvailableTimeSlot[] => {
  const dateKey = date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  let slots = mockTimeSlots[dateKey] || [];
  
  // Filter by employee if specified
  if (employeeId !== undefined) {
    slots = slots.filter(slot => slot.employeeId === employeeId);
  }
  
  return slots;
};

// Helper function to check if a date has available slots (optionally for specific employee)
export const hasAvailableSlotsForDate = (date: Date, employeeId?: string | number): boolean => {
  const slots = getAvailableSlotsForDate(date, employeeId);
  return slots.some(slot => slot.isAvailable);
};

// Helper to find first available date (for "Jump to first available day")
export const findFirstAvailableDate = (employeeId?: string | number, startDate: Date = new Date()): Date | null => {
  const maxDaysToCheck = 30; // Check up to 30 days ahead
  
  for (let i = 0; i < maxDaysToCheck; i++) {
    const checkDate = new Date(startDate);
    checkDate.setDate(startDate.getDate() + i);
    
    if (hasAvailableSlotsForDate(checkDate, employeeId)) {
      return checkDate;
    }
  }
  
  return null; // No available date found in the next 30 days
};

// Generate available time slots using real studio working hours
export const getAvailableSlotsForDateWithRealHours = (
  date: Date, 
  employeeId: number | undefined,
  studioHours: { [key: string]: { slots: Array<{ startTime: string; endTime: string }>, isWorkingDay: boolean } }
): IAvailableTimeSlot[] => {
  if (!studioHours) return [];
  
  const timeSlots = generateTimeSlotsFromWorkingHours(studioHours, date, 30);
  const dayOfWeek = date.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const isToday = date.toDateString() === new Date().toDateString();
  
  if (timeSlots.length === 0) {
    return []; // Not a working day
  }
  
  // Generate slots for all available employees (1, 2, 3) or specific employee
  const employeeIds = employeeId !== undefined ? [employeeId] : [1, 2, 3];
  const slots: IAvailableTimeSlot[] = [];
  
  timeSlots.forEach(time => {
    employeeIds.forEach(empId => {
      let isAvailable = true;
      
      // Different availability patterns based on mock logic
      if (isToday) {
        isAvailable = Math.random() > 0.3; // 70% available
      } else if (isWeekend) {
        isAvailable = Math.random() > 0.2; // 80% available on weekends
      } else {
        isAvailable = Math.random() > 0.15; // 85% available on weekdays
      }
      
      // Some employees have days off (keep mock logic for now)
      const dayIndex = Math.floor((date.getTime() - new Date().getTime()) / (24 * 60 * 60 * 1000));
      if (dayIndex % 7 === 3 && empId === 3) isAvailable = false; // Adam has Wednesdays off
      if (dayIndex % 14 === 7 && empId === 2) isAvailable = false; // Ивайло has every other Sunday off
      
      slots.push({
        time,
        isAvailable,
        employeeId: empId
      });
    });
  });
  
  return slots;
};

// Helper to check if a date has available slots using real hours
export const hasAvailableSlotsForDateWithRealHours = (
  date: Date, 
  employeeId: number | undefined,
  studioHours: { [key: string]: { slots: Array<{ startTime: string; endTime: string }>, isWorkingDay: boolean } }
): boolean => {
  const slots = getAvailableSlotsForDateWithRealHours(date, employeeId, studioHours);
  return slots.some(slot => slot.isAvailable);
};

// Helper to find first available date using real studio hours
export const findFirstAvailableDateWithRealHours = (
  employeeId: number | undefined, 
  studioHours: { [key: string]: { slots: Array<{ startTime: string; endTime: string }>, isWorkingDay: boolean } },
  startDate: Date = new Date()
): Date | null => {
  const maxDaysToCheck = 30; // Check up to 30 days ahead
  
  for (let i = 0; i < maxDaysToCheck; i++) {
    const checkDate = new Date(startDate);
    checkDate.setDate(startDate.getDate() + i);
    
    if (hasAvailableSlotsForDateWithRealHours(checkDate, employeeId, studioHours)) {
      return checkDate;
    }
  }
  
  return null; // No available date found in the next 30 days
};